import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSelector } from "app/hooks";
import { selectCurrentUser } from "features/auth/auth-slice";
import { wantsCallAlert } from "features/calls/dialstack/call-alerts.util";
import { useDialstackSoftphone } from "features/calls/dialstack/softphone/use-dialstack-softphone.hook";
import {
  PracticeCall,
  useGetPracticeCallsQuery,
  useGetVoicemailsQuery
} from "services/dialstack";

import { useBrowserNotifications } from "./browser-notifications.context";

// Voicemail has no browser websocket event yet, so the softphone-owning tab
// polls history. A minute is plenty for a callback queue and keeps the
// practice-wide feed cheap: one request per query per tab. Missed calls now
// arrive as `call.missed` frames (missed-call-notifications.component.tsx);
// the poll stays as the fallback for an older backend, and the delivery
// ledger (keyed by call id) keeps it from bannering a call twice.
export const HISTORY_POLL_INTERVAL_MS = 60_000;
export const HISTORY_PAGE_SIZE = 50;
// The voicemail row lands a little after its call ends (recording upload plus
// webhook). A voicemail-status call waits one poll cycle so the recording, when
// there is one, alerts as "New voicemail" instead of "Missed call".
export const VOICEMAIL_GRACE_MS = HISTORY_POLL_INTERVAL_MS + 15_000;
// "voicemail" is an unanswered inbound call that reached a mailbox — the
// Calls workspace labels it "Missed — voicemail".
const MISSED_CALL_STATUSES = ["no_answer", "busy", "failed", "voicemail"];

const pollingOptions = {
  pollingInterval: HISTORY_POLL_INTERVAL_MS,
  skipPollingIfUnfocused: false,
  refetchOnMountOrArgChange: true
};

const isSince = (timestamp: string | null, since: number) => {
  if (!timestamp) return false;
  const time = Date.parse(timestamp);
  return Number.isFinite(time) && time >= since;
};

const callerName = (call: PracticeCall) =>
  (call.patient
    ? `${call.patient.preferred_name || call.patient.first_name} ${call.patient.last_name}`
    : call.from_number) || "Unknown caller";

// The backend pushes call.incoming and call.missed to web clients today, but
// not voicemail. Poll terminal history in the softphone-owning tab until a
// voicemail websocket event exists. Ringing and chat do NOT depend on this timer.
export default function VoiceHistoryNotifications() {
  const { enabled } = useDialstackSoftphone();
  const { permission, preferences } = useBrowserNotifications();
  if (
    !enabled ||
    permission !== "granted" ||
    (!preferences.missedCalls && !preferences.voicemail)
  )
    return null;
  return <VoiceHistoryObserver />;
}

function VoiceHistoryObserver() {
  const { notify, preferences } = useBrowserNotifications();
  const user = useAppSelector(selectCurrentUser);
  const navigate = useNavigate();
  const since = useRef(Date.now());
  const seen = useRef(new Set<string>());
  // Calls that reached a mailbox, keyed by call id, waiting for their voicemail.
  const pendingMissed = useRef(
    new Map<number, ReturnType<typeof setTimeout>>()
  );
  const voicemailCallIds = useRef(new Set<number>());
  const { currentData: calls } = useGetPracticeCallsQuery(
    { direction: "inbound", limit: HISTORY_PAGE_SIZE, offset: 0 },
    { skip: !preferences.missedCalls, ...pollingOptions }
  );
  const { currentData: myVoicemails } = useGetVoicemailsQuery(
    { limit: HISTORY_PAGE_SIZE, offset: 0 },
    { skip: !preferences.voicemail, ...pollingOptions }
  );
  const { currentData: sharedVoicemails } = useGetVoicemailsQuery(
    { limit: HISTORY_PAGE_SIZE, offset: 0, shared: true },
    { skip: !preferences.voicemail, ...pollingOptions }
  );

  const markNew = useCallback((id: string, timestamp: string | null) => {
    if (!isSince(timestamp, since.current) || seen.current.has(id))
      return false;
    seen.current.add(id);
    if (seen.current.size > 500)
      seen.current.delete(seen.current.values().next().value!);
    return true;
  }, []);

  useEffect(() => {
    const pending = pendingMissed.current;
    return () => pending.forEach((timer) => clearTimeout(timer));
  }, []);

  // Voicemail runs before calls so a recording seen in the same render wins.
  useEffect(() => {
    const voicemails = [...(myVoicemails ?? []), ...(sharedVoicemails ?? [])];
    voicemailCallIds.current = new Set<number>();
    for (const voicemail of voicemails) {
      if (voicemail.call === null) continue;
      voicemailCallIds.current.add(voicemail.call);
      clearTimeout(pendingMissed.current.get(voicemail.call));
      pendingMissed.current.delete(voicemail.call);
    }
    for (const voicemail of voicemails) {
      if (
        voicemail.is_read ||
        !markNew(`voicemail:${voicemail.id}`, voicemail.created_at)
      )
        continue;
      notify({
        type: "voicemail",
        id: String(voicemail.id),
        title: "New voicemail",
        body: voicemail.from_name || voicemail.from_number || "Unknown caller",
        onClick: () => navigate(`/calls?voicemail=${voicemail.id}`)
      });
    }
  }, [myVoicemails, sharedVoicemails, markNew, notify, navigate]);

  useEffect(() => {
    for (const call of calls ?? []) {
      if (
        call.answered_at ||
        !MISSED_CALL_STATUSES.includes(call.status ?? "") ||
        // A location this user chose not to hear about (ENG-2513).
        !wantsCallAlert(user, call.clinic) ||
        !markNew(`call:${call.id}`, call.ended_at) ||
        voicemailCallIds.current.has(call.id)
      )
        continue;
      const alertMissedCall = () => {
        pendingMissed.current.delete(call.id);
        notify({
          type: "missedCalls",
          id: String(call.id),
          title: "Missed call",
          body: callerName(call),
          onClick: () => navigate(`/calls?call=${call.id}`)
        });
      };
      if (call.status !== "voicemail") {
        alertMissedCall();
        continue;
      }
      pendingMissed.current.set(
        call.id,
        setTimeout(alertMissedCall, VOICEMAIL_GRACE_MS)
      );
    }
  }, [calls, markNew, notify, navigate, user]);
  return null;
}
